import React, { useState, useContext } from 'react';
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { Uid } from "../../utils.tsx";
import { ScanType } from '../../scanTypes.ts';
import { SampleConfigurationSet } from '../../sampleSets.ts';
import { BeamlineContext } from '../../beamlineProvider.tsx';
import { SampleSetsContext } from '../../sampleSetsProvider.tsx';
import { SelectingStatus } from '../../components/inputAutocomplete.tsx'
import { ScanTypeAutocomplete, ScanTypeSearchFunctions } from '../../components/scanTypeAutocomplete.tsx';
import { createNewSampleAndParameterValues } from '../../sampleSets.ts';


const AddSamples: React.FC = () => {

  var { setSlug } = useParams();

  const beamlineContext = useContext(BeamlineContext);
  const sampleSetsContext = useContext(SampleSetsContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>("Sample");
  const [quantity, setQuantity] = useState<string>("1");
  const [description, setDescription] = useState<string>("");

  const firstTypeSlug =
          beamlineContext.scanTypes.typeSlugsInDisplayOrder[0] || null;
  const initialScanTypeValue = firstTypeSlug ? beamlineContext.scanTypes.typesBySlug.get(firstTypeSlug)! : null;
  const [scanTypeValue, setScanTypeValue] = useState<ScanType | null>(initialScanTypeValue)

  const [validQuantity, setValidQuantity] = useState<boolean>(true);
  const [validName, setValidName] = useState<boolean>(true);
  const [uniqueName, setUniqueName] = useState<boolean>(true);
  const [validAllInput, setValidAllInput] = useState<boolean>(true);

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  function getSet(): SampleConfigurationSet | undefined {
    if (!setSlug) { return undefined; }
    return sampleSetsContext.sets.getBySlug(setSlug);
  }

  function clickedOpen() {
    const thisSet = getSet();
    if (!inProgress && !isOpen && thisSet) {
      setIsOpen(true);
      const goodNames = thisSet.generateUniqueNames(newName, 1);
      setNewName(goodNames[0]);
      validate(quantity, goodNames[0], scanTypeValue);
    }
  }

  function changedQuantity(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setQuantity(v);
    validate(v, newName, scanTypeValue);
  }

  function changedName(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setNewName(v);
    validate(quantity, v, scanTypeValue);
  }

  function changedDescription(c:React.ChangeEvent<HTMLInputElement>) {
    const v = c.target.value;
    setDescription(v);
  }

  function validate(quantity:string, name:string, scanType: ScanType | null) {
    var validQuantity:boolean = true;
    var validName:boolean = true;
    var uniqueName:boolean = true;

    if (parseInt(quantity, 10) < 1) {
      validQuantity = false;
    }

    const trimmed = name.toString().trim();
    if (trimmed.length < 1) {
      validName = false;
    } else {
      const thisSet = getSet();
      if (thisSet) {
        if (thisSet.all().some((c) => c.name == trimmed)) {
          validName = false;
          uniqueName = false;
        }
      }
    }

    setValidQuantity(validQuantity);
    setValidName(validName);
    setUniqueName(uniqueName);
    setValidAllInput(validQuantity && validName && (scanType !== null));
  }

  async function pressedSubmit() {
    const thisSet = getSet();
    if (!thisSet) { return; }

    setSubmitErrorMessage(null);
    setInProgress(true);

    var count = Math.max(parseInt(quantity, 10), 1);
    // These are new config objects but they don't have real Uid values.
    // Those will be added when they're round-tripped to the server below.
    const filteredNewName = newName.replace(/[^A-Za-z0-9\-_]/g, "_");
    const newSampleDtos = thisSet.generateNewConfigurationsWithDefaults(
        count, scanTypeValue!.slug, filteredNewName, description
      );

    count = 0;
    var newConfigs = [];
    var error: string | null = null;

    while (count < newSampleDtos.length && (error === null)) {
      const result = await createNewSampleAndParameterValues(newSampleDtos[count].sample, newSampleDtos[count].values);
      if (result.success) {
        newConfigs.push(result.response!);
      } else {
        error = result.message || "";
      }
      count++;
    }

    thisSet.addWithHistory(newConfigs);
    sampleSetsContext.changed();
    setInProgress(false);
    if (error !== null) {
      setSubmitErrorMessage(error);
    } else {
      setIsOpen(false);
    }
  }


  function clickedClose() {
    if (!inProgress && isOpen) { setSubmitErrorMessage(null); setIsOpen(false); }
  }


  const scanTypeSearchFunctions:ScanTypeSearchFunctions = {
    itemSelected: (item: ScanType) => { setScanTypeValue(item); validate(quantity, newName, item); return SelectingStatus.Success },
    selectedNone: () => { setScanTypeValue(null); validate(quantity, newName, null); return SelectingStatus.Success }
  };


  return (
    <div>

      <button className="button" onClick={ clickedOpen }>Add Samples</button>

      <div id="modal-add-sample" className={ isOpen ? "modal is-active" : "modal" }>
        <div className="modal-background"></div>

        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Add Samples</p>
            <button className="delete" aria-label="close" onClick={ clickedClose }></button>

          </header>
          <section className="modal-card-body">

            <div className="field">
              <label className="label">How Many?</label>
              <div className="control has-icons-right">
                <input className={ validQuantity ? "input" : "input is-danger" }
                  type="number"
                  placeholder="#"
                  value={ quantity }
                  onChange={ changedQuantity } />
                <span className="icon is-small is-right">
                  { validQuantity || (<FontAwesomeIcon icon={faExclamationTriangle} color="darkred" />) }
                </span>
              </div>
              { validQuantity || (<p className="help is-danger">Must enter a number &gt; 0</p>) }
            </div>

            <div className="field">
              { validQuantity && (parseInt(quantity, 10) > 1) ?
                (<label className="label">Prefix</label>) :
                (<label className="label">Name</label>)
              }
              <div className="control has-icons-right">
                <input className={ validName ? "input" : "input is-danger" }
                  type="text"
                  placeholder="Unique name"
                  value={ newName }
                  onChange={ changedName } />
                <span className="icon is-small is-right">
                  { validName || (<FontAwesomeIcon icon={faExclamationTriangle} color="darkred" />) }
                </span>
              </div>
              { validName || (uniqueName ?
                              (<p className="help is-danger">This name is invalid</p>) :
                              (<p className="help is-danger">This name is not unique</p>))
              }
            </div>

            <div className="field">
              <label className="label">Description</label>
              <div className="control">
                <input className="input"
                  type="text"
                  placeholder="Optional description"
                  value={ description }
                  onChange={ changedDescription } />
              </div>
            </div>

            <div className="field">
              <label className="label">Scan Type</label>
                <ScanTypeAutocomplete
                    selectedItem={scanTypeValue}
                    searchFunctions={scanTypeSearchFunctions}
                    allowedScanTypes={beamlineContext.scanTypes.typeSlugsInDisplayOrder}
                  />
            </div>

            <div className="buttons">
              <input
                type="button"
                className="button is-success"
                onClick={ pressedSubmit }
                disabled={ inProgress || !validAllInput }
                value="Add"
              />
              <button className="button" onClick={ clickedClose }>Cancel</button>
            </div>

            { submitErrorMessage && (
              <article className="message is-danger">
                <div className="message-body">
                  { submitErrorMessage }
                </div>
              </article>
            )}

          </section>

        </div>
      </div>

    </div>
  )
}
export default AddSamples
